/**
 * Maps claim draft + licence slice to structured collision FNOL API body fields.
 * @module claims/collisionFnolSubmitFields
 */

import type { ClaimFlowDraft, SingleVehicleCollisionTargetId } from './claimDraftStorage'
import {
  buildMotorFnolDriverSubmitFields,
  type MotorFnolDriverLicenceSlice,
} from './motorFnolDriverSubmitFields'

/** Licence / DOB fields merged for submit (see {@link pickMvDriverLicenceFieldsForClaimSubmit}). */
export type CollisionSubmitDriverLicenceSlice = MotorFnolDriverLicenceSlice

/** Structured fields shared by single-vehicle and multi-vehicle collision submit APIs. */
export interface StructuredCollisionFnolSubmitFields {
  incidentDateDdMmYyyy: string | null
  incidentTimeHm: string | null
  incidentTimeAmPm: 'AM' | 'PM' | null
  incidentAddressUnknown: boolean | null
  incidentAddressSearch: string | null
  incidentStreet: string | null
  incidentSuburb: string | null
  incidentPostcode: string | null
  incidentState: string | null
  gstRegistered?: boolean
  itcEntitled?: boolean | null
  itcPercentage?: number | null
  multiVehicleDriverCaptureMode: ClaimFlowDraft['multiVehicleDriverCaptureMode']
  multiVehicleAttendedAtCollision?: boolean | null
  multiVehiclePrimaryDriverDriving?: boolean | null
  multiVehicleSecondaryOnPolicyDriving?: boolean | null
  multiVehicleListedDriverId?: string | null
  driverFirstName: string | null
  driverLastName: string | null
  driverDobDdMmYyyy: string | null
  licenceNumber: string | null
  licenceExpiryDdMmYyyy: string | null
  yearsLicensed: number | null
}

/**
 * Builds structured incident, GST, and driver fields for collision FNOL POST bodies.
 */
export function buildStructuredCollisionFnolSubmitFields(
  d: ClaimFlowDraft,
  driverLicence: CollisionSubmitDriverLicenceSlice | null,
  options: {
    /** When true, includes GST/ITC fields (windscreen-only in the portal). */
    includeGst?: boolean
    gstRegistered?: boolean
    itcEntitled?: boolean | null
    itcPercentage?: number | null
    includeAttendedFlag?: boolean
    /** Single-vehicle flow never uses unattended capture; omit invalid mode for API typing. */
    forSingleVehicle?: boolean
  }
): StructuredCollisionFnolSubmitFields {
  const driverFields = buildMotorFnolDriverSubmitFields(d, driverLicence)

  const fields: StructuredCollisionFnolSubmitFields = {
    incidentDateDdMmYyyy: d.incidentDateDdMmYyyy ?? null,
    incidentTimeHm: d.incidentTimeHm ?? null,
    incidentTimeAmPm: d.incidentTimeAmPm ?? null,
    incidentAddressUnknown: d.incidentAddressUnknown ?? null,
    incidentAddressSearch: d.incidentAddressSearch ?? null,
    incidentStreet: d.incidentStreet ?? null,
    incidentSuburb: d.incidentSuburb ?? null,
    incidentPostcode: d.incidentPostcode ?? null,
    incidentState: d.incidentState ?? null,
    multiVehicleDriverCaptureMode: driverFields.multiVehicleDriverCaptureMode,
    ...(options.includeAttendedFlag
      ? { multiVehicleAttendedAtCollision: d.multiVehicleAttendedAtCollision ?? null }
      : {}),
    multiVehiclePrimaryDriverDriving: driverFields.multiVehiclePrimaryDriverDriving,
    multiVehicleSecondaryOnPolicyDriving: driverFields.multiVehicleSecondaryOnPolicyDriving,
    multiVehicleListedDriverId: driverFields.multiVehicleListedDriverId,
    driverFirstName: driverFields.driverFirstName,
    driverLastName: driverFields.driverLastName,
    driverDobDdMmYyyy: driverFields.driverDobDdMmYyyy,
    licenceNumber: driverFields.licenceNumber,
    licenceExpiryDdMmYyyy: driverFields.licenceExpiryDdMmYyyy,
    yearsLicensed: driverFields.yearsLicensed,
  }

  if (options.includeGst && typeof options.gstRegistered === 'boolean') {
    fields.gstRegistered = options.gstRegistered
    fields.itcEntitled = options.itcEntitled ?? null
    fields.itcPercentage = options.itcPercentage ?? null
  }

  return fields
}

/** Driver capture modes allowed on single-vehicle collision submit API. */
export type SingleVehicleDriverCaptureModeForSubmit = 'primary' | 'listed' | 'manual' | null

/**
 * Strips `none_unattended` so spread into {@link SubmitSingleVehicleClaimPayload} type-checks.
 */
export function singleVehicleStructuredSubmitFields(
  fields: StructuredCollisionFnolSubmitFields
): Omit<StructuredCollisionFnolSubmitFields, 'multiVehicleDriverCaptureMode' | 'multiVehicleAttendedAtCollision'> & {
  multiVehicleDriverCaptureMode: SingleVehicleDriverCaptureModeForSubmit
} {
  const mode = fields.multiVehicleDriverCaptureMode
  const captureMode: SingleVehicleDriverCaptureModeForSubmit =
    mode === 'primary' || mode === 'listed' || mode === 'manual' ? mode : null
  const { multiVehicleAttendedAtCollision: _attended, multiVehicleDriverCaptureMode: _mode, ...rest } =
    fields
  return { ...rest, multiVehicleDriverCaptureMode: captureMode }
}

/** Driver POST fields for single-vehicle collision (and fire attended driver step). */
export type SingleVehicleDriverOnlySubmitFields = Pick<
  ReturnType<typeof singleVehicleStructuredSubmitFields>,
  | 'multiVehicleDriverCaptureMode'
  | 'multiVehiclePrimaryDriverDriving'
  | 'multiVehicleSecondaryOnPolicyDriving'
  | 'multiVehicleListedDriverId'
  | 'driverFirstName'
  | 'driverLastName'
  | 'driverDobDdMmYyyy'
  | 'licenceNumber'
  | 'licenceExpiryDdMmYyyy'
  | 'yearsLicensed'
>

/**
 * Same driver resolution as single-vehicle collision review submit (fire FNOL reuses this).
 */
export function singleVehicleDriverOnlySubmitFields(
  d: ClaimFlowDraft,
  driverLicence: CollisionSubmitDriverLicenceSlice | null
): SingleVehicleDriverOnlySubmitFields {
  const fields = singleVehicleStructuredSubmitFields(
    buildStructuredCollisionFnolSubmitFields(d, driverLicence, { forSingleVehicle: true })
  )
  return {
    multiVehicleDriverCaptureMode: fields.multiVehicleDriverCaptureMode,
    multiVehiclePrimaryDriverDriving: fields.multiVehiclePrimaryDriverDriving,
    multiVehicleSecondaryOnPolicyDriving: fields.multiVehicleSecondaryOnPolicyDriving,
    multiVehicleListedDriverId: fields.multiVehicleListedDriverId,
    driverFirstName: fields.driverFirstName,
    driverLastName: fields.driverLastName,
    driverDobDdMmYyyy: fields.driverDobDdMmYyyy,
    licenceNumber: fields.licenceNumber,
    licenceExpiryDdMmYyyy: fields.licenceExpiryDdMmYyyy,
    yearsLicensed: fields.yearsLicensed,
  }
}

export type { SingleVehicleCollisionTargetId }
